import { joinRootSuffix, noteLabel, type NoteNotation } from "./notation";
import type { DetectedKey } from "./key";

export type TonalityMode = "major" | "minor";

export type Tonality = {
  tonic: string;
  mode: TonalityMode;
};

const PITCH_CLASS: Record<string, number> = {
  C: 0, "C#": 1, Db: 1, D: 2, "D#": 3, Eb: 3,
  E: 4, F: 5, "F#": 6, Gb: 6, G: 7, "G#": 8, Ab: 8,
  A: 9, "A#": 10, Bb: 10, B: 11,
};

/** Major tonalities ordered by the circle of fifths. */
export const MAJOR_TONALITIES: Tonality[] = [
  "C", "G", "D", "A", "E", "B", "F#", "Db", "Ab", "Eb", "Bb", "F",
].map((tonic) => ({ tonic, mode: "major" as const }));

/** Minor tonalities ordered by the circle of fifths. */
export const MINOR_TONALITIES: Tonality[] = [
  "A", "E", "B", "F#", "C#", "G#", "D#", "Bb", "F", "C", "G", "D",
].map((tonic) => ({ tonic, mode: "minor" as const }));

const RELATIVE_MINOR_OF_MAJOR: Record<string, string> = {
  C: "A", G: "E", D: "B", A: "F#", E: "C#", B: "G#", "F#": "D#",
  F: "D", Bb: "G", Eb: "C", Ab: "F", Db: "Bb",
};

const RELATIVE_MAJOR_OF_MINOR: Record<string, string> = {
  A: "C", E: "G", B: "D", "F#": "A", "C#": "E", "G#": "B", "D#": "F#",
  D: "F", G: "Bb", C: "Eb", F: "Ab", Bb: "Db",
};

export function relativeTonality(t: Tonality): Tonality {
  if (t.mode === "major") {
    return { tonic: RELATIVE_MINOR_OF_MAJOR[t.tonic] ?? t.tonic, mode: "minor" };
  }

  return { tonic: RELATIVE_MAJOR_OF_MINOR[t.tonic] ?? t.tonic, mode: "major" };
}

const MODE_LABEL_FR: Record<TonalityMode, string> = {
  major: "majeur",
  minor: "mineur",
};

const MODE_SHORT_FR: Record<TonalityMode, string> = {
  major: "maj",
  minor: "m",
};

export function tonalityLabel(t: Tonality, notation: NoteNotation): string {
  return `${noteLabel(t.tonic, notation)} ${modeLabel(t.mode)}`;
}

/**
 * Le mode seul : « majeur », « mineur ».
 *
 * Pour les surfaces qui écrivent la tonique et son mode dans deux encres —
 * la fiche du sas met la tonique en violet — et qui ne peuvent donc pas
 * recevoir la phrase entière. Le nom sort d'ici et non de l'appelant, sans quoi
 * deux tables diraient « mineur » chacune de son côté.
 */
export function modeLabel(mode: TonalityMode): string {
  return MODE_LABEL_FR[mode];
}

/**
 * L'abrégé : « Ré m », « Dm ».
 *
 * La tonique suit la notation choisie et le suffixe se colle comme dans un
 * chiffrage — collé derrière une lettre, séparé d'une insécable derrière un
 * nom de solfège. C'est la même règle que `chordSymbolLabel`, et c'est elle
 * qui fait que l'abrégé anglais se lit « Dm » plutôt que « D m ».
 */
export function tonalityShortLabel(
  t: Tonality,
  notation: NoteNotation,
): string {
  return joinRootSuffix(
    noteLabel(t.tonic, notation),
    MODE_SHORT_FR[t.mode],
    notation,
  );
}

export function tonalityFromDetectedKey(key: DetectedKey | null): Tonality | null {
  if (!key) {
    return null;
  }

  const tonic = key.label.split(" ")[0];

  return { tonic, mode: key.mode };
}

export function tonalityEquals(a: Tonality | null, b: Tonality | null): boolean {
  if (a === b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  return a.tonic === b.tonic && a.mode === b.mode;
}

export function tonicPitchClass(tonic: string): number {
  return PITCH_CLASS[tonic] ?? 0;
}

/** Smallest signed semitone delta between two tonics, in [-6, +6]. */
export function shortestSemitones(from: Tonality, to: Tonality): number {
  const fromPc = tonicPitchClass(from.tonic);
  const toPc = tonicPitchClass(to.tonic);
  const diff = ((toPc - fromPc) % 12 + 12) % 12;

  return diff > 6 ? diff - 12 : diff;
}
